import { useTranslation } from 'react-i18next';
import { PieChart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { DefectBreakdown, REDO_REASON_LABELS } from '../types';
import { EmptyState } from './EmptyState';

// Known reason colors — stable per reason value so charts compare cleanly
// across periods even though the SQL orders by count DESC.
const KNOWN_REASON_COLORS: Record<string, string> = {
  print_defect: 'hsl(0, 72%, 51%)',
  press_issue: 'hsl(25, 95%, 53%)',
  misprint: 'hsl(38, 92%, 50%)',
  stain_mark: 'hsl(262, 52%, 55%)',
  wrong_size: 'hsl(221, 83%, 53%)',
  wrong_color: 'hsl(280, 60%, 55%)',
  damaged_garment: 'hsl(346, 77%, 50%)',
  // Catcher-side wrong-item (Threadheads' configured value)
  wrong_item_fulfilled: 'hsl(15, 85%, 55%)',
  // Print-station garment-issue reasons (BLU-385)
  garment_contamination: 'hsl(180, 60%, 40%)',
  manufacturer_defect: 'hsl(305, 65%, 50%)',
  wrong_item_picked: 'hsl(50, 90%, 50%)',
  other: 'hsl(215, 14%, 60%)',
};

// Color palette for custom reasons not in the known map
const CUSTOM_REASON_PALETTE = [
  'hsl(160, 60%, 45%)',
  'hsl(45, 85%, 50%)',
  'hsl(190, 70%, 45%)',
  'hsl(330, 65%, 50%)',
  'hsl(100, 50%, 45%)',
  'hsl(200, 65%, 50%)',
];

function getReasonColor(reason: string, customIndex: number): string {
  return KNOWN_REASON_COLORS[reason] ?? CUSTOM_REASON_PALETTE[customIndex % CUSTOM_REASON_PALETTE.length] ?? 'hsl(215, 14%, 60%)';
}

interface DefectBreakdownChartProps {
  data: DefectBreakdown[];
  isLoading?: boolean;
}

export function DefectBreakdownChart({ data, isLoading }: DefectBreakdownChartProps) {
  const { t } = useTranslation('production');
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('qa.defectsByReason')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('qa.defectsByReason')}</CardTitle>
        </CardHeader>
        <CardContent>
          <EmptyState
            icon={PieChart}
            title={t('qa.noDefectsTitle')}
            description={t('qa.noDefectsDescription')}
          />
        </CardContent>
      </Card>
    );
  }

  let customColorIndex = 0;

  const items = data.map((item) => {
    const label = REDO_REASON_LABELS[item.reason] ||
      item.reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const isCustom = !(item.reason in KNOWN_REASON_COLORS);
    const color = getReasonColor(item.reason, isCustom ? customColorIndex++ : 0);
    return { ...item, label, color };
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('qa.defectsByReason')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.reason}
              className="flex items-center gap-3 py-1.5"
            >
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-sm flex-1 truncate">{item.label}</span>
              <span className="text-sm font-medium tabular-nums">{item.count}</span>
              <Badge variant="secondary" className="text-xs tabular-nums min-w-[48px] justify-center">
                {item.percentage}%
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
